import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Button, Dialog, DialogTrigger, Popover } from "react-aria-components";

import externalLinkIcon from "@/assets/figma/answer-sources/external-link.svg";
import sourceSiteIcon from "@/assets/figma/answer-sources/source-site.svg";

gsap.registerPlugin(useGSAP);


export interface MerryAnswerSource {
  supportStatus?: "DIRECT_SUPPORT" | "DIRECT_OBSERVATION" | "TRANSFERABLE_HYPOTHESIS" | "HYPOTHESIS" | "UNVERIFIED";
  excerpt?: string;
  iconUrl?: string;
  id: string;
  label: string;
  observedAt?: string;
  url: string;
}

export interface MerryAnswerSourcesProps {
  readState?: "loading" | "error" | "ready";
  onRetry?: () => void;
  "data-testid"?: string;
  className?: string;
  sources: ReadonlyArray<MerryAnswerSource>;
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function formatObservedAt(value: string | undefined) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("zh-CN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

function SourcePreview({
  source,
}: {
  source: MerryAnswerSource;
}) {
  const [failedURL, setFailedURL] = useState<string | null>(null);
  const sourceURL = source.iconUrl;
  const previewURL = sourceURL && sourceURL !== failedURL ? sourceURL : sourceSiteIcon;

  return (
    <img
      alt=""
      aria-hidden="true"
      className="merry-answer-sources__preview"
      onError={() => {
        if (sourceURL) setFailedURL(sourceURL);
      }}
      src={previewURL}
    />
  );
}

export function MerryAnswerSources({
  "data-testid": testId,
  className,
  sources,
  readState = "ready",
  onRetry,
}: MerryAnswerSourcesProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hasMountedRef = useRef(false);
  const popoverId = useId();
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const visualState = isOpen
    ? "open"
    : isHovered || isFocused
      ? "emphasized"
      : "rest";
  const previews = useMemo(() => sources.slice(0, 3), [sources]);
  const updateOpen = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        const popover = popoverRef.current;
        if (isClosing && popover) {
          gsap.killTweensOf(popover);
          gsap.to(popover, {
            autoAlpha: 1,
            duration: prefersReducedMotion() ? 0 : 0.18,
            ease: "power2.out",
            overwrite: true,
            pointerEvents: "auto",
            scale: 1,
            transformOrigin: "top right",
            y: 0,
          });
        }
        setIsClosing(false);
        setIsOpen(true);
        return;
      }
      if (!isOpen || isClosing) return;
      const popover = popoverRef.current;
      if (!popover || prefersReducedMotion()) {
        setIsOpen(false);
        setIsClosing(false);
        requestAnimationFrame(() => triggerRef.current?.focus());
        return;
      }
      setIsClosing(true);
      gsap.killTweensOf(popover);
      gsap.to(popover, {
        autoAlpha: 0,
        duration: 0.14,
        ease: "power2.in",
        overwrite: true,
        pointerEvents: "none",
        scale: 0.98,
        transformOrigin: "top right",
        y: -4,
        onComplete: () => {
          setIsOpen(false);
          setIsClosing(false);
          requestAnimationFrame(() => triggerRef.current?.focus());
        },
      });
    },
    [isClosing, isOpen],
  );

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      updateOpen(false);
    };
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      updateOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [isOpen, updateOpen]);

  useEffect(() => {
    if (sources.length > 0 || !isOpen) return;
    const frame = requestAnimationFrame(() => {
      setIsClosing(false);
      setIsOpen(false);
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen, sources.length]);

  useGSAP(
    () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const label = trigger.querySelector<HTMLElement>(
        ".merry-answer-sources__label",
      );
      const icons = Array.from(
        trigger.querySelectorAll<HTMLElement>(
          ".merry-answer-sources__preview + .merry-answer-sources__preview",
        ),
      );
      const emphasized = visualState === "emphasized";
      const open = visualState === "open";
      const values = {
        backgroundColor: open
          ? "rgb(25 38 43 / 10%)"
          : emphasized
            ? "rgb(25 38 43 / 5%)"
            : "rgb(25 38 43 / 0%)",
        color: open || emphasized ? "rgb(25 38 43 / 100%)" : "rgb(25 38 43 / 60%)",
        iconMargin: emphasized ? 2 : -6,
        width: emphasized ? 106 : 90,
      };
      const duration =
        prefersReducedMotion() || !hasMountedRef.current ? 0 : 0.22;

      gsap.killTweensOf([trigger, label, ...icons]);
      gsap.to(trigger, {
        backgroundColor: values.backgroundColor,
        duration,
        ease: "power2.out",
        overwrite: true,
        width: values.width,
      });
      if (label) {
        gsap.to(label, {
          color: values.color,
          duration,
          ease: "power2.out",
          overwrite: true,
        });
      }
      gsap.to(icons, {
        duration,
        ease: "power2.out",
        marginLeft: values.iconMargin,
        overwrite: true,
      });
      hasMountedRef.current = true;
    },
    { dependencies: [visualState] },
  );

  useEffect(() => {
    if (!isOpen) return;
    let animatedPopover: HTMLDivElement | null = null;
    const frame = requestAnimationFrame(() => {
      const popover = popoverRef.current;
      if (!popover) return;
      animatedPopover = popover;
      if (prefersReducedMotion()) {
        gsap.set(popover, { autoAlpha: 1, scale: 1, y: 0 });
        return;
      }
      gsap.fromTo(
        popover,
        { autoAlpha: 0, scale: 0.98, y: -4 },
        {
          autoAlpha: 1,
          duration: 0.2,
          ease: "power3.out",
          overwrite: true,
          scale: 1,
          transformOrigin: "top right",
          y: 0,
        },
      );
    });
    return () => {
      cancelAnimationFrame(frame);
      if (animatedPopover) gsap.killTweensOf(animatedPopover);
    };
  }, [isOpen]);

  if (readState === "error") return <span role="alert">依据读取失败<Button onPress={onRetry}>重试</Button></span>;
  if (readState === "loading" && sources.length === 0) return <span role="status">依据加载中…</span>;
  if (sources.length === 0) return null;

  return (
    <span
      className={["merry-answer-sources", className].filter(Boolean).join(" ")}
      data-figma-node-id={isOpen ? "711:97757" : isHovered ? "705:97082" : "705:96823"}
      data-answer-sources-closing={isClosing ? "true" : "false"}
      data-testid={testId}
    >
      <DialogTrigger
        isOpen={isOpen || isClosing}
        onOpenChange={updateOpen}
      >
        <Button
          aria-label={`查看 ${sources.length} 个来源`}
          className="merry-answer-sources__trigger"
          data-answer-sources-state={visualState}
          onFocusChange={setIsFocused}
          onHoverChange={setIsHovered}
          ref={triggerRef}
        >
          <span className="merry-answer-sources__label">
            {sources.length} 来源
          </span>
          <span aria-hidden="true" className="merry-answer-sources__previews">
            {previews.map((source) => (
              <SourcePreview
                key={source.id}
                source={source}
              />
            ))}
          </span>
        </Button>

        <Popover
          className="merry-answer-sources__popover"
          isNonModal
          offset={0}
          placement="bottom end"
          ref={popoverRef}
          shouldFlip
        >
          <div className="merry-answer-sources__panel" id={popoverId}>
            <Dialog aria-label="来源网址" className="merry-answer-sources__dialog">
              <div className="merry-answer-sources__title">来源网址</div>
              <ul aria-label="来源列表" className="merry-answer-sources__list">
                {sources.map((source) => (
                  <AnswerSourceItem key={source.id} source={source} />
                ))}
              </ul>
            </Dialog>
          </div>
        </Popover>
      </DialogTrigger>
    </span>
  );
}

function AnswerSourceItem({ source }: { source: MerryAnswerSource }) {
  const observedAt = formatObservedAt(source.observedAt);
  const itemRef = useRef<HTMLAnchorElement>(null);

  function animateItem(active: boolean) {
    const item = itemRef.current;
    const main = item?.querySelector<HTMLElement>(
      ".merry-answer-sources__item-main",
    );
    const external = item?.querySelector<HTMLElement>(
      ".merry-answer-sources__external-icon",
    );
    if (!item || !main || !external) return;
    const duration = prefersReducedMotion() ? 0 : 0.18;
    gsap.killTweensOf([item, main, external]);
    gsap.to(item, {
      backgroundColor: active
        ? "rgb(25 38 43 / 5%)"
        : "rgb(25 38 43 / 0%)",
      duration,
      ease: "power2.out",
      overwrite: true,
    });
    gsap.to(main, {
      duration,
      ease: "power2.out",
      overwrite: true,
      width: active ? 272 : 242,
    });
    gsap.to(external, {
      autoAlpha: active ? 0 : 1,
      duration,
      ease: "power2.out",
      marginLeft: active ? 0 : 6,
      overwrite: true,
      width: active ? 0 : 24,
    });
  }

  return (
    <li className="merry-answer-sources__list-item">
      <a
        className="merry-answer-sources__item"
        data-figma-node-id="711:98112"
        href={source.url}
        onBlur={() => animateItem(false)}
        onFocus={() => animateItem(true)}
        onPointerEnter={() => animateItem(true)}
        onPointerLeave={() => animateItem(false)}
        ref={itemRef}
        rel="noopener noreferrer"
        target="_blank"
      >
        <span className="merry-answer-sources__item-main">
          <img
            alt=""
            aria-hidden="true"
            className="merry-answer-sources__site-icon"
            src={sourceSiteIcon}
          />
          <span className="merry-answer-sources__item-content">
            <strong>{source.label}</strong>
            {source.supportStatus ? <span>{{ DIRECT_SUPPORT: "直接支持", DIRECT_OBSERVATION: "直接观测", TRANSFERABLE_HYPOTHESIS: "类比，待验证", HYPOTHESIS: "假设", UNVERIFIED: "资格未核实" }[source.supportStatus]}</span> : null}
            {source.excerpt || observedAt ? (
              <span className="merry-answer-sources__item-meta">
                {source.excerpt ? <span>{source.excerpt}</span> : null}
                {source.excerpt && observedAt ? (
                  <span aria-hidden="true">·</span>
                ) : null}
                {observedAt ? (
                  <time dateTime={source.observedAt}>{observedAt}</time>
                ) : null}
              </span>
            ) : null}
          </span>
        </span>
        <img
          alt=""
          aria-hidden="true"
          className="merry-answer-sources__external-icon"
          src={externalLinkIcon}
        />
      </a>
    </li>
  );
}
